import { useCallback, useEffect, useRef, useState } from "react";
import {
  toSourceTypeId,
  type OrgSelectionApi,
  type SourceOrg,
} from "@onetasklist/shared/sources/source-settings";
import { useApi } from "@api/useApi";
import { createDebugLogger } from "@utils/debugLog";
import type {
  SelectOrgDialogViewModel,
  SelectOrgTarget,
} from "./SelectOrgDialog.ViewModel.types";

const log = createDebugLogger("auth-popup:select-org");

/**
 * The source registry is heterogeneous — most modules have no org selection at
 * all — so narrow at runtime rather than asserting the shape.
 */
const isOrgSelectionApi = (api: unknown): api is OrgSelectionApi =>
  !!api &&
  typeof (api as OrgSelectionApi).getOrgs === "function" &&
  typeof (api as OrgSelectionApi).selectOrg === "function";

/**
 * Drives the environment picker shown in the main window after the auth popup
 * returns a discovery token. Runs in the opener — not the popup — so the API
 * calls carry the user's JWT.
 *
 * `onAuthUrl` receives the provider URL for the second, environment-scoped
 * OAuth leg. The caller owns opening the popup for it.
 */
export const useSelectOrgDialogViewModel = (
  target: SelectOrgTarget | null,
  onAuthUrl: (authUrl: string) => void,
  onCancel: () => void,
): SelectOrgDialogViewModel => {
  // Held in a ref so the load effect keys off the target alone. useApi() can
  // hand back a fresh object per render (it returns whatever ApiContext holds),
  // and depending on its identity would re-run the effect on every render.
  const api = useApi();
  const apiRef = useRef(api);

  // Declared before the load effect so the ref is current by the time it runs.
  useEffect(() => {
    apiRef.current = api;
  }, [api]);

  const [orgs, setOrgs] = useState<SourceOrg[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [hasSubmitError, setHasSubmitError] = useState(false);

  const boardId = target?.boardId;
  const connectionId = target?.connectionId;
  const sourceTypeId = target?.sourceTypeId;

  /**
   * CDS, Altus and P4TW each expose org selection on their own routes, so the
   * API comes from the source registry rather than being hardcoded.
   */
  const getOrgApi = useCallback((): OrgSelectionApi | null => {
    const registryId = sourceTypeId ? toSourceTypeId(sourceTypeId) : null;
    if (!registryId) {
      log("No registry id for source", { sourceTypeId });
      return null;
    }

    const module =
      apiRef.current.connectionSourceSettings.tryGetSourceApi(registryId);

    if (!isOrgSelectionApi(module)) {
      log("Source has no org selection API", { sourceTypeId });
      return null;
    }

    return module;
  }, [sourceTypeId]);

  useEffect(() => {
    if (!boardId || !connectionId) {
      setOrgs([]);
      setSelectedOrgId(null);
      setHasLoadError(false);
      setHasSubmitError(false);
      setIsLoading(false);
      return;
    }

    const orgApi = getOrgApi();
    if (!orgApi) {
      setHasLoadError(true);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setHasLoadError(false);
    log("Fetching orgs", { boardId, connectionId, sourceTypeId });

    orgApi
      .getOrgs(boardId, connectionId)
      .then((data) => {
        if (cancelled) return;
        log("Orgs loaded", { count: data.length });
        setOrgs(data);
        if (data.length === 1) {
          log("Auto-selecting the only org", { orgId: data[0].id });
          setSelectedOrgId(data[0].id);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        log("Failed to load orgs", err);
        setHasLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [getOrgApi, boardId, connectionId, sourceTypeId]);

  const submit = useCallback(async (): Promise<void> => {
    const org = orgs.find((o) => o.id === selectedOrgId);
    if (!org || !boardId || !connectionId) {
      log("submit called with no matching selected org", { selectedOrgId });
      return;
    }

    const orgApi = getOrgApi();
    if (!orgApi) {
      setHasSubmitError(true);
      return;
    }

    log("Submitting org selection", { orgId: org.id, boardId, connectionId });
    setIsSubmitting(true);
    setHasSubmitError(false);

    try {
      const { authUrl } = await orgApi.selectOrg(boardId, connectionId, {
        orgId: org.id,
        orgUrl: org.url,
        orgFriendlyName: org.friendlyName,
        orgType: org.organizationType,
      });

      log("Org selected, handing auth URL back to the caller");
      onAuthUrl(authUrl);
    } catch (err) {
      log("Failed to submit org selection", err);
      setHasSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [getOrgApi, boardId, connectionId, onAuthUrl, orgs, selectedOrgId]);

  return {
    orgs,
    selectedOrgId,
    isLoading,
    isSubmitting,
    hasLoadError,
    hasSubmitError,
    hasNoOrgs: !isLoading && !hasLoadError && orgs.length === 0,
    canSubmit: !!selectedOrgId && !isSubmitting,
    setSelectedOrgId,
    submit,
    cancel: onCancel,
  };
};
